import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import Utils from 'ui-component-library/helpers/Utils';
import { GenericObject, HttpRequestMethods } from '@/types';
import { useUserStore } from '@/store/UserStore';
import _ from 'lodash';
import { Options } from 'bulma-toast';
import { useContextStore } from '@/store/ContextStore';
import { getActivePinia } from 'pinia';

const apiRoot = '/api';
const apiVersion = 'v4.3';

/**
 * Attach the shell's token to our own calls.
 *
 * This UI has no login of its own — in the target picture the planet's ingress
 * (`/a12n`) checks and passes `X-Auth-User` through. As long as that ingress is
 * missing, the call has to identify itself, otherwise nothing can be checked in
 * front of the api-facade at all: the browser sends neither a cookie (no
 * `withCredentials`) nor anything else here.
 *
 * The source is the shell's login store. Host and remote share Pinia as a
 * singleton (`vite.config.federation.js`), so the `auth` store of
 * `@link11/auth-sdk` lives in the same instance. The access deliberately goes
 * **through the active Pinia instance rather than through an import**: the
 * remote also runs standalone, and the SDK does not exist there — an import
 * would be a hard dependency that breaks the standalone build.
 *
 * Read per request, not once: the token is renewed silently.
 */
function shellAccessToken(): string | undefined {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pinia: any = getActivePinia();
    const token = pinia?.state?.value?.auth?.user?.access_token;
    return typeof token === 'string' && token ? token : undefined;
  } catch {
    return undefined;
  }
}

axios.interceptors.request.use((config) => {
  const token = shellAccessToken();
  if (token && !config.headers?.Authorization) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Use lambdas so that spy/mock replacements on the axios object are picked up at call time
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const axiosMethodsMap: Partial<{ [_key in HttpRequestMethods]: any }> = {
  GET: (...args: Parameters<typeof axios.get>) => axios.get(...args),
  PUT: (...args: Parameters<typeof axios.put>) => axios.put(...args),
  POST: (...args: Parameters<typeof axios.post>) => axios.post(...args),
  DELETE: (...args: Parameters<typeof axios.delete>) => axios.delete(...args),
};

const extractErrorMessage = (data: string | GenericObject): string | null => {
  if (_.isString(data)) {
    return data;
  }

  if (!_.isObject(data)) {
    return null;
  }

  const errorFields = ['detail', 'message', 'error'];
  for (const field of errorFields) {
    if (_.isString(data[field])) {
      return data[field];
    }
    if (_.isObject(data[field]) && !Array.isArray(data[field])) {
      const val = data[field] as GenericObject;
      return val.message ?? val.detail ?? null;
    }
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    return data.errors
      .map((err: string | GenericObject) => {
        if (_.isString(err)) return err;
        return err.message || err.detail || JSON.stringify(err);
      })
      .join('; ');
  }

  return null;
};

const processRequest = (requestParams: IRequestParams) => {
  // Get correct axios method
  if (!requestParams.methodName) {
    requestParams.methodName = 'GET';
  } else {
    requestParams.methodName = <HttpRequestMethods>requestParams.methodName.toUpperCase();
  }
  const axiosMethod = axiosMethodsMap[requestParams.methodName];
  if (!axiosMethod) {
    console.error(`Attempted sending unrecognized request method ${requestParams.methodName}`);
    return;
  }

  console.log(`Sending ${requestParams.methodName} request to url ${requestParams.url}`);

  const contextStore = useContextStore();
  let fullUrl = requestParams.url;
  // Add remote Origin only for shell mode with relative URLs; absolute URLs are passed through as-is
  if (contextStore.isShell && contextStore.remoteOrigin && !fullUrl.startsWith('http')) {
    const urlPath = fullUrl.startsWith('/') ? fullUrl : `/${fullUrl}`;
    fullUrl = `${contextStore.remoteOrigin}${urlPath}`;
  }
  let request;
  if (requestParams.data) {
    if (requestParams.config) {
      request = axiosMethod(fullUrl, requestParams.data, requestParams.config);
    } else {
      request = axiosMethod(fullUrl, requestParams.data);
    }
  } else {
    if (requestParams.config) {
      request = axiosMethod(fullUrl, requestParams.config);
    } else {
      request = axiosMethod(fullUrl);
    }
  }
  request = request
    .then((response: AxiosResponse) => {
      // Reset user idle tracker
      const userStore = useUserStore();
      userStore.idleTrackerReset();
      // Follow redirect
      if (response?.headers?.location) {
        sessionStorage.setItem('redirectingToLogin', 'true');
        window.location.href = response.headers.location;
        return;
      }
      const encodedURL = encodeURI(requestParams.url.split('?')[0]);
      if (response?.request?.responseURL && !response.request.responseURL.includes(encodedURL)) {
        sessionStorage.setItem('redirectingToLogin', 'true');
        window.location.href = response.request.responseURL;
        return;
      }
      // Toast message
      if (requestParams.successMessage) {
        const toast: Options = {
          message: requestParams.successMessage,
          type: 'is-success',
        };
        if (requestParams.messageDuration) {
          toast.duration = requestParams.messageDuration;
        }
        Utils.toast(toast);
      }
      return response;
    })
    .catch((error: AxiosError) => {
      const data: string | GenericObject = error?.response?.data;
      const serverMessage = requestParams.displayServerFailureMessage
        ? extractErrorMessage(data)
        : null;
      const failureMessage = serverMessage || requestParams.failureMessage;

      if (failureMessage && !requestParams.disableFailureToast) {
        const toast: Options = {
          message: failureMessage,
          type: 'is-danger',
        };
        if (requestParams.messageDuration) {
          toast.duration = requestParams.messageDuration;
        }
        Utils.toast(toast);
      }

      if (typeof requestParams.onFail === 'function') {
        requestParams.onFail(error);
      }
      console.error(error);
    });
  return request;
};

export interface IRequestParams {
  methodName: HttpRequestMethods;
  url: string;
  host?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  config?: AxiosRequestConfig;
  successMessage?: string;
  failureMessage?: string;
  messageDuration?: number;
  displayServerFailureMessage?: boolean;
  /** When true, skips the default failure toast (caller handles via onFail) */
  disableFailureToast?: boolean;
  onFail?: (error: AxiosError) => void;
}

const sendRequest = (requestParams: IRequestParams) => {
  requestParams = _.cloneDeep(requestParams);
  if (requestParams.host) {
    const base = requestParams.host.replace(/\/$/, '');
    requestParams.url = `${base}${apiRoot}/${apiVersion}/${requestParams.url}`;
  } else {
    requestParams.url = `${apiRoot}/${apiVersion}/${requestParams.url}`;
  }
  return processRequest(requestParams);
};

const sendAbsoluteRequest = (requestParams: IRequestParams) => {
  requestParams = _.cloneDeep(requestParams);
  return processRequest(requestParams);
};

export default {
  name: 'RequestsUtils',
  sendRequest,
  apiRoot,
  apiVersion,
  sendAbsoluteRequest,
};
